import {
  PdfBitmap,
  PdfDocument,
  PdfPageOrientation,
  PdfPageSettings,
  type PdfSection,
  SizeF,
} from "@syncfusion/ej2-pdf-export";
import {
  type DocumentEditor,
  type DocumentEditorContainer,
  type ImageFormat,
} from "@syncfusion/ej2-react-documenteditor";
import { saveAs } from "file-saver";
import JSZip from "jszip";

import { exportDocument } from "./apiRequests";
import { useEffect, useMemo, useRef } from "react";
import { AxiosError } from "axios";
import { downloadDocument, uploadDocument } from "./v2/requests";
import { type DocumentVersionSummary } from "./types";

export const Utils = {
  downloadPDF: (documentEditor: DocumentEditor): Promise<void> =>
    new Promise((resolve, reject) => {
      try {
        const pdfdocument = new PdfDocument();
        documentEditor.documentEditorSettings.printDevicePixelRatio = 2;
        const { pageCount } = documentEditor;
        let loadedPage = 0;
        for (let i = 1; i <= pageCount; i++) {
          const format: ImageFormat = "image/jpeg" as ImageFormat;
          // Getting pages as image
          const image = documentEditor.exportAsImage(i, format);
          image.onload = function () {
            const imageHeight = parseInt(
              image.style.height.toString().replace("px", ""),
            );
            const imageWidth = parseInt(
              image.style.width.toString().replace("px", ""),
            );
            const section: PdfSection =
              pdfdocument.sections.add() as PdfSection;
            const settings: PdfPageSettings = new PdfPageSettings(0);
            if (imageWidth > imageHeight) {
              settings.orientation = PdfPageOrientation.Landscape;
            }
            settings.size = new SizeF(imageWidth, imageHeight);
            section.setPageSettings(settings);
            const page = section.pages.add();
            const graphics = page.graphics;
            const imageStr = image.src.replace("data:image/jpeg;base64,", "");
            const pdfImage = new PdfBitmap(imageStr);
            graphics.drawImage(pdfImage, 0, 0, imageWidth, imageHeight);
            loadedPage += 1;
            if (loadedPage === pageCount) {
              pdfdocument.save(`${document.title}.pdf`);
              resolve();
            }
          };
        }
      } catch (error) {
        reject(
          error instanceof Error
            ? error
            : new Error("An error occurred while downloading the document"),
        );
      }
    }),
  createDocumentPdf: async (container: DocumentEditorContainer) => {
    const pdfDocument: PdfDocument = new PdfDocument();
    const count: number = container.documentEditor.pageCount;
    container.documentEditor.documentEditorSettings.printDevicePixelRatio = 2;
    const format: ImageFormat = "image/jpeg" as ImageFormat;
    const arrPages = Array.from(Array(count)).map((_, i) => {
      return new Promise((resolve) => {
        const image = container.documentEditor.exportAsImage(i + 1, format);
        image.onload = function () {
          const imageHeight = parseInt(
            image.style.height.toString().replace("px", ""),
          );
          const imageWidth = parseInt(
            image.style.width.toString().replace("px", ""),
          );
          const section: PdfSection = pdfDocument.sections.add() as PdfSection;
          const settings: PdfPageSettings = new PdfPageSettings(0);
          if (imageWidth > imageHeight) {
            settings.orientation = PdfPageOrientation.Landscape;
          }
          settings.size = new SizeF(imageWidth, imageHeight);
          section.setPageSettings(settings);
          const page = section.pages.add();
          const graphics = page.graphics;
          const imageStr = image.src.replace("data:image/jpeg;base64,", "");
          const pdfImage = new PdfBitmap(imageStr);
          graphics.drawImage(pdfImage, 0, 0, imageWidth, imageHeight);
          resolve(null);
        };
      });
    });
    for (const result of arrPages) {
      await Promise.resolve(result);
    }
    return await pdfDocument.save();
  },
  saveZipDocuments: async (
    documentVersions: DocumentVersionSummary[],
    updateProgress: (value: number) => void,
  ) => {
    const zip = new JSZip();
    let count = 0;

    for (const { id, title, fileType, document } of documentVersions) {
      try {
        let blob = await downloadDocument(id);
        let fileExtension = fileType;

        if (fileType === "sfdt") {
          const blobString = await blob.text();
          const response = await exportDocument(blobString);
          blob = response.data;
          fileExtension = "docx";
        }

        zip
          .folder(`${document.category.title}`)
          ?.folder(`${document.tier.title}`)
          ?.file(`${title}_${id}.${fileExtension}`, blob);
      } catch (error) {
        if (error instanceof AxiosError && error.code !== "ERR_BAD_REQUEST") {
          // Ignore bad request error for now. They are caused by the document being empty.
          throw error;
        }
      }
      count++;
      updateProgress(count);
    }
    await zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, "documents");
    });
  },
};

export async function saveDocument(
  id: number,
  documentEditor?: DocumentEditor,
  onSuccess?: () => void,
  onError?: () => void,
) {
  if (documentEditor) {
    try {
      await uploadDocument(id, documentEditor.serialize());
      if (onSuccess) onSuccess();
    } catch {
      if (onError) onError();
    }
  }
}

/**
 * Debounce a callback function
 */
export function useDebounce<
  Callback extends (...args: Parameters<Callback>) => ReturnType<Callback>,
>(callback: Callback, delay: number) {
  const callbackRef = useRef(callback);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Create a stable debounced function wrapper
  return useMemo(() => {
    return (...args: Parameters<Callback>) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // Access ref in the timeout callback, not during render
        callbackRef.current(...args);
      }, delay);
    };
  }, [delay]);
}
